/**
 * Adapter: istniejące call site Karuzeli (Studio*) renderują OVL (Op*).
 * Nie zmieniaj API Studio* bez migracji call site.
 */
import type { ReactNode } from "react";
import { OpCallout } from "@/components/op/OpCallout";
import { OpNowBar } from "@/components/op/OpNowBar";
import { OpSection } from "@/components/op/OpSection";
import { cn } from "@/lib/utils";
import { trackLabel, type StudioNowAction, type StudioSectionRole, type StudioTrack, publicationKindLabel, type PublicationKind } from "@/lib/studioVoice";
import type { StudioTab } from "@/lib/carouselStudioMeta";

function toOpRole(role: StudioSectionRole): "now" | "path" | "optional" | "info" {
  if (role === "next") return "now";
  if (role === "optional") return "optional";
  if (role === "info") return "info";
  return "path";
}

export function StudioTrackChip({
  track,
  className,
}: {
  track: StudioTrack;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
        track === "operator" && "border-slate-400 bg-white text-slate-800",
        track === "ai" && "border-brand bg-brand/15 text-brand",
        !track && "border-l-4 border-orange-500 border-orange-200 bg-white text-orange-950",
        className,
      )}
    >
      {trackLabel(track)}
    </span>
  );
}

export function StudioKindChip({
  kind,
  className,
}: {
  kind: PublicationKind | string;
  className?: string;
}) {
  const k = (kind || "") as PublicationKind;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
        k === "carousel" && "border-slate-400 bg-white text-slate-800",
        k === "feed_post" && "border-sky-400 bg-sky-50 text-sky-900",
        k === "czasotekst" && "border-violet-400 bg-violet-50 text-violet-900",
        !k && "border-l-4 border-orange-500 border-orange-200 bg-white text-orange-950",
        className,
      )}
    >
      {publicationKindLabel(k)}
    </span>
  );
}

export function StudioNowBar({
  action,
  currentTab,
  onGo,
  className,
}: {
  action: StudioNowAction;
  currentTab: StudioTab;
  onGo: (tab: StudioTab, sectionId?: string) => void;
  className?: string;
}) {
  const sameTab = action.tab === currentTab;
  const canJump = !sameTab || Boolean(action.sectionId);
  return (
    <div id="studio-now" className={className}>
      <OpNowBar
        sticky={false}
        label={action.label}
        detail={action.detail}
        actionLabel={sameTab ? "Pokaż" : "Idź"}
        onAction={
          canJump
            ? () => {
                onGo(action.tab, action.sectionId);
                if (sameTab && action.sectionId && typeof document !== "undefined") {
                  window.setTimeout(() => {
                    document.getElementById(action.sectionId!)?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 50);
                }
              }
            : undefined
        }
      />
    </div>
  );
}

export function StudioSection({
  role,
  title,
  hint,
  id,
  defaultOpen,
  children,
  className,
  actions,
}: {
  role: StudioSectionRole;
  title?: string;
  hint?: string;
  id?: string;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
}) {
  return (
    <OpSection
      role={toOpRole(role)}
      title={title}
      hint={hint}
      id={id}
      defaultOpen={defaultOpen}
      className={className}
      actions={actions}
    >
      {children}
    </OpSection>
  );
}

export function StudioInfoStrip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <OpCallout variant="help" showLabel={false} className={cn("text-xs", className)}>
      <div className="flex flex-wrap gap-2">{children}</div>
    </OpCallout>
  );
}
